import { getServiceDiscriminator, mapValues } from 'services/utils'
import { NAMES_DISCRIMINATORS_MAP } from 'services/utils/name-maps'
import { ProductServiceDiscriminator } from 'types/common'
import {
  GeneralBriefQuestion,
  BriefQuestionBase,
  BriefQuestion,
} from '../servicesconfig'
import { EventBrief, ServiceBrief } from './../eventBrief'
import {
  EventItem,
  MappedEnrichedEvent,
  UnMappedEnrichedEvent,
} from './types/internal/event-models'
import { EventBriefQuestion } from './types/internal/services'

/**
 * Used to filter the general questions according to the services that are selected
 *
 * ES. If a food service is selected and the general question is related to the food service this fn returns true
 * @param selectedServicesDiscriminators - array of selected services discriminators
 * @param proServices - array of professional services made of professional sections
 * @param generalQuestion - a single general brief question
 * @returns true if the question is correlated with the selected services false else
 */
export const isGeneralQuestionBoundToSelectedServices =
  (selectedServicesDiscriminators: string[], proServices: string[]) =>
  (generalQuestion: GeneralBriefQuestion) => {
    const hasSelectedServices = generalQuestion.activeWhenServices.some(
      (service) => selectedServicesDiscriminators.includes(service)
    )

    const hasSelectedProfessionalServices =
      generalQuestion.activeWhenServices.some((s) => proServices.includes(s))

    return hasSelectedServices || hasSelectedProfessionalServices
  }

export const isQuestionCompatibleWithSelectedProducts =
  (
    serviceDiscriminator: ProductServiceDiscriminator,
    event: UnMappedEnrichedEvent
  ) =>
  (question: BriefQuestion) => {
    const productsNameService = NAMES_DISCRIMINATORS_MAP[serviceDiscriminator]
    const productsOfService =
      serviceDiscriminator === 'ProfessionalConfigurator'
        ? event['professionals'].flatMap(
            (professional) => professional.selections
          )
        : event[productsNameService]
    return (
      !question.products.length ||
      question.products.some((product) =>
        productsOfService.some((p) => p.id === product)
      )
    )
  }

const mergeQuestionsAndAnswers = <T extends BriefQuestionBase>(
  responses: EventBrief,
  serviceBrief: T[],
  discriminator: string
) =>
  serviceBrief.map((q) => {
    const serviceSection = responses.find(
      (r) => r.supplieredService === discriminator
    )

    const questionResponse = serviceSection?.questionResponses.find(
      (r) => r.id === q.id
    )

    return {
      ...q,
      response: questionResponse?.response ?? '',
      status: questionResponse?.status ?? 'active',
    }
  })

const findBySupplieredServiceService = (name: string) => (r: ServiceBrief) =>
  r.supplieredService === name

const getGeneralServiceWithBrief = (
  surveyResponses: EventBrief,
  generalQuestions: GeneralBriefQuestion[]
) => {
  const mergedBriefQuestions = mergeQuestionsAndAnswers(
    surveyResponses,
    generalQuestions,
    'GeneralConfigurator'
  )
  return {
    briefQuestions: mergedBriefQuestions,
    discriminator: 'GeneralConfigurator' as const,
  }
}

export const parseEventMultiplex = (
  event: UnMappedEnrichedEvent
): MappedEnrichedEvent => {
  const { serviceConfig, surveyResponses, ...parsedEvent } = event
  const { purchasedServicesArray, productServices, professionals } = parsedEvent
  const {
    id,
    professionalService: { services },
    generalService: generalServiceConfig,
    ...namedServicesConfig
  } = serviceConfig

  const selectedServicesDiscriminators = purchasedServicesArray.map(
    getServiceDiscriminator
  )
  const selectedProfessionalNames = professionals.map((p) => p.name)

  const generalQuestions = generalServiceConfig.brief.filter(
    isGeneralQuestionBoundToSelectedServices(
      selectedServicesDiscriminators,
      selectedProfessionalNames
    )
  )

  const generalServiceWithBrief = getGeneralServiceWithBrief(
    surveyResponses,
    generalQuestions
  )

  const eventNamedServicesWithBrief = mapValues((service, servName) => {
    const { serviceDiscriminator, brief } = namedServicesConfig[servName]
    const isAmongSelectedServices =
      selectedServicesDiscriminators.includes(serviceDiscriminator)

    const compatibleBriefQuestions = brief.filter(
      isQuestionCompatibleWithSelectedProducts(
        serviceDiscriminator as ProductServiceDiscriminator,
        event
      )
    )

    const mergedBriefQuestions = isAmongSelectedServices
      ? mergeQuestionsAndAnswers(
          surveyResponses,
          compatibleBriefQuestions,
          serviceDiscriminator
        )
      : []

    return {
      ...service,
      briefConfirmed: !!surveyResponses.find(
        findBySupplieredServiceService(serviceDiscriminator)
      )?.briefConfirmed,
      briefQuestions: mergedBriefQuestions,
      discriminator: serviceDiscriminator,
    }
  }, productServices)

  const professionalsWithBrief = professionals.map((p) => {
    const briefQuestions =
      services
        .find((proService) => proService.section.name === p.name)
        ?.brief.filter(
          isQuestionCompatibleWithSelectedProducts(
            'ProfessionalConfigurator',
            event
          )
        ) ?? []

    const mergedBriefQuestions = mergeQuestionsAndAnswers(
      surveyResponses,
      briefQuestions,
      p.name
    )

    return {
      ...p,
      briefConfirmed: !!surveyResponses.find(
        findBySupplieredServiceService(p.name)
      )?.briefConfirmed,
      briefQuestions: mergedBriefQuestions,
      discriminator: p.name,
    }
  })

  return {
    ...parsedEvent,
    surveyResponses,
    professionals: professionalsWithBrief,
    generalService: generalServiceWithBrief,
    productServices: eventNamedServicesWithBrief,
  }
}

const getBriefResponses = <
  T extends { discriminator: string; briefQuestions?: EventBriefQuestion[] }
>(
  service: T
) => ({
  supplieredService: service.discriminator,
  questionResponses:
    service.briefQuestions?.map(({ text, response = '', id, status }) => ({
      id,
      question: text,
      response,
      status,
    })) ?? [],
})

const hasBriefQuestions = <T extends { briefQuestions?: unknown[] }>(
  service: T
): service is T => !!service.briefQuestions?.length

const hasProductSelection =
  (event: EventItem) =>
  <T extends { discriminator: string }>({ discriminator }: T) =>
    !!event[
      NAMES_DISCRIMINATORS_MAP[discriminator as ProductServiceDiscriminator]
    ]?.length

const hasProfessionalSelections =
  ({ professionals }: EventItem) =>
  ({ supplieredService }: ServiceBrief) =>
    !!professionals.find((p) => p.name === supplieredService)

export const mapEventToAPI = (req: MappedEnrichedEvent) => {
  const selectedProductServicesArray = Object.values(
    req.productServices
  ).filter(hasProductSelection(req))

  const selectedServiceDiscriminators = [
    ...selectedProductServicesArray.map((s) => s.discriminator),
    ...req.professionals.map((p) => p.discriminator),
  ]

  const serviceBriefBundle: EventBrief = selectedProductServicesArray
    .filter(hasBriefQuestions)
    .map(getBriefResponses)

  const professionalServiceBriefBundle: EventBrief = req.professionals
    .filter(hasBriefQuestions)
    .map(getBriefResponses)
    .filter(hasProfessionalSelections(req))

  const nonGeneralBriefBundles = [
    ...serviceBriefBundle,
    ...professionalServiceBriefBundle,
  ]

  const generalServiceFilteredQuestions = (
    req.generalService?.briefQuestions ?? []
  ).filter((q) =>
    q.activeWhenServices.some((service) =>
      selectedServiceDiscriminators.includes(service)
    )
  )

  const generalServiceBriefBundle = !!generalServiceFilteredQuestions.length
    ? getBriefResponses({
        ...req.generalService!,
        briefQuestions: generalServiceFilteredQuestions,
      })
    : undefined

  return {
    ...req,
    surveyResponses: [
      // if there is no general service there is no object with question-answers to send
      ...(!!generalServiceBriefBundle ? [generalServiceBriefBundle] : []),
      ...nonGeneralBriefBundles,
    ],
  }
}
